<script setup lang="ts">
import { useGameStore } from '@/stores/gameStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import TutorialFrame from '@/components/Interface/Modal/Modals/Tutorial/TutorialFrame.vue'
import AlertBox from '@/components/Elements/Alerts/AlertBox.vue'
import { handleNext } from '../tutorialSteps'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()

const gameHasStarted = computed(() => gameStore.turn > 1)

const handleNextStep = async () => {
  await handleNext('tutorial_start')
}

// Reactive current time for countdown
const now = ref(new Date())
let interval: number | undefined

// Start interval on mount
onMounted(() => {
  interval = window.setInterval(() => {
    now.value = new Date()
  }, 1000)
})

// Cleanup on unmount
onUnmounted(() => {
  if (interval) {
    clearInterval(interval)
  }
})

// Calculate time left until arena starts
const timeLeft = computed(() => {
  const startTime = new Date(gameStore.startTime)
  const totalSeconds = Math.max(0, Math.floor((startTime.getTime() - now.value.getTime()) / 1000))

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const pad = (n: number) => n.toString().padStart(2, "0")

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
})

// Format local start time
const localStartTime = computed(() => {
  const startTime = new Date(gameStore.startTime)
  return startTime.toLocaleString(undefined, {
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
})


</script>

<template>
  <TutorialFrame 
    v-if="!gameHasStarted"
    video-url="https://cdn.ferion.com/videos/advisors/temperate/exploration.webm"
  >
    <template #text>
      <p class="pb-2">
        {{ t('tutorial.start.hello') }}
      </p>
      <p class="pb-2">
        {{ t('tutorial.start.preGame.ferionBlocked') }}
      </p>
      <p class="pb-2">
        {{ t('tutorial.start.preGame.readyForOrders') }}
      </p>
    </template>

    <template #alerts>
      <AlertBox type="info">
        <span v-html="t('tutorial.start.preGame.arenaStart', { time: '<span class=\'text-green-500\'>' + localStartTime + '</span>', countdown: '<span class=\'text-green-500\'>' + timeLeft + '</span>' })"></span>
      </AlertBox>
      <AlertBox type="warning" class="md:hidden">
        <p class="pb-2">{{ t('tutorial.start.mobileWarning') }}</p>
        {{ t('tutorial.start.mobileRecommendation') }}
      </AlertBox>
    </template>

    <template #buttons>
      <ActionButton @click="handleNextStep">
        {{ t('common.actions.understood') }}
      </ActionButton>
    </template>
  </TutorialFrame>

  <!-- In-game version (Day > 1) -->
  <TutorialFrame v-else
    video-url="https://cdn.ferion.com/videos/advisors/temperate/exploration.webm"
  >
    <template #text>
        <p class="pb-2">
          {{ t('tutorial.start.hello') }}
        </p>
        <p class="pb-2">
          {{ t('tutorial.start.inGame.notRight') }}
        </p>
        <p class="pb-2">
          {{ t('tutorial.start.inGame.followOrders') }}
        </p>
    </template>

    <template #alerts>
      <AlertBox type="info">
        <span v-html="t('tutorial.start.inGame.alreadyUnderway', { day: gameStore.turn, disadvantage: '<span class=\'text-red-400\'>' + t('tutorial.start.inGame.disadvantage') + '</span>' })"></span>
      </AlertBox>
      <AlertBox type="error" class="md:hidden">
        <p class="pb-2">{{ t('tutorial.start.mobileWarning') }}</p>
        {{ t('tutorial.start.mobileRecommendation') }}
      </AlertBox>
    </template>

    <template #buttons>
      <ActionButton @click="handleNextStep">
        {{ t('common.actions.understood') }}
      </ActionButton>
    </template>
  </TutorialFrame>
</template>

<style scoped lang="scss">

</style>